import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UIToggleGroup from 'UIComponents/input/UIToggleGroup';
import UIRadioInput from 'UIComponents/input/UIRadioInput';
import UIHelpIcon from 'UIComponents/icon/UIHelpIcon';
import Small from 'UIComponents/elements/Small';
import UIList from 'UIComponents/list/UIList';
import { ScrollContainer } from 'foundations-components/transitional/components/ScrollContainer';
import { getId as getDashboardId
// @ts-expect-error migrate upstream
} from 'dashboard-lib/public/dashboard/dashboard-schema';
import { AppliedDashboardFiltersList } from '../../dashboard/header/dashboardFilters/AppliedDashboardFiltersList';
import { selectDashboardFilters, selectDashboardFrequency } from '../../../ducks/applied-dashboard-filters';
import { DashboardFiltersGroupedByDataSourceList } from '../../dashboard/header/dashboardFilters/DashboardFiltersGroupedByDataSourceList';
import { useFetchEmailOnlyFilterSet } from '../../../lib/savedDashboardFilters/useFetchEmailOnlyFilterSet';
import { isFailed } from 'reporting-data/asyncData/AsyncData';
import UIAlert from 'UIComponents/alert/UIAlert';
import { useCreateEmailOnlyAppliedFilterSet } from './use-create-email-only-applied-filter-set';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var SendWithFiltersOptions;
(function (SendWithFiltersOptions) {
  SendWithFiltersOptions[SendWithFiltersOptions["NONE"] = 0] = "NONE";
  SendWithFiltersOptions[SendWithFiltersOptions["CURRENT_APPLIED_FILTERS"] = 1] = "CURRENT_APPLIED_FILTERS";
  SendWithFiltersOptions[SendWithFiltersOptions["SAVED_FILTER_SET"] = 2] = "SAVED_FILTER_SET";
  SendWithFiltersOptions[SendWithFiltersOptions["PREVIOUS_SENT_FILTERS"] = 3] = "PREVIOUS_SENT_FILTERS";
})(SendWithFiltersOptions || (SendWithFiltersOptions = {}));
export const ApplySavedDashboardFiltersEmailOptions = ({
  dashboard: immutableDashboard,
  onApplyDashboardFilters,
  filterSetId: initialFilterSetId,
  isUnsupportedDataExportType
}) => {
  const dashboard = immutableDashboard.toJS();
  const dashboardId = getDashboardId(dashboard);
  const {
    emailFilters
  } = useFetchEmailOnlyFilterSet(dashboard, initialFilterSetId);
  const appliedFilters = useSelector(selectDashboardFilters(dashboardId));
  const appliedFrequency = useSelector(selectDashboardFrequency(dashboardId));
  const {
    emailOnlyFilterSetId,
    asyncState: createEmailOnlyAsyncState,
    createEmailOnlyFilterSet
  } = useCreateEmailOnlyAppliedFilterSet(dashboardId);
  const hasInitialFilterSet = !!initialFilterSetId;
  const [selectedSendWithFiltersOption, setSelectedSendWithFiltersOption] = useState(!hasInitialFilterSet ? SendWithFiltersOptions.NONE : hasInitialFilterSet ? SendWithFiltersOptions.PREVIOUS_SENT_FILTERS : SendWithFiltersOptions.CURRENT_APPLIED_FILTERS);
  useEffect(() => {
    if (isUnsupportedDataExportType) {
      setSelectedSendWithFiltersOption(SendWithFiltersOptions.NONE);
    }
  }, [isUnsupportedDataExportType]);
  const handleSendWithCurrentAppliedFilters = () => {
    setSelectedSendWithFiltersOption(SendWithFiltersOptions.CURRENT_APPLIED_FILTERS);
    if (emailOnlyFilterSetId) {
      onApplyDashboardFilters(emailOnlyFilterSetId);
    } else {
      createEmailOnlyFilterSet().then(createdFilterSetId => {
        onApplyDashboardFilters(createdFilterSetId);
      }).catch(error => {
        console.error(error);
        setSelectedSendWithFiltersOption(SendWithFiltersOptions.NONE);
      });
    }
  };
  const createEmailOnlyFilterSetRequestFailed = isFailed(createEmailOnlyAsyncState);
  const hasAppliedFilters = appliedFilters && appliedFilters.length > 0 || appliedFrequency != null;
  return /*#__PURE__*/_jsx("div", {
    children: /*#__PURE__*/_jsx(UIFormControl, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "email.dashboard.prompt.contextLabel"
      }),
      children: /*#__PURE__*/_jsx(UIToggleGroup, {
        name: "apply-saved-db-filters-toggle",
        children: /*#__PURE__*/_jsxs(UIList, {
          children: [/*#__PURE__*/_jsx(UIRadioInput, {
            defaultChecked: !hasInitialFilterSet,
            checked: selectedSendWithFiltersOption === SendWithFiltersOptions.NONE,
            onChange: () => {
              setSelectedSendWithFiltersOption(SendWithFiltersOptions.NONE);
              onApplyDashboardFilters();
            },
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "email.dashboard.withoutImprovedFilters"
            })
          }), hasInitialFilterSet && emailFilters.length > 0 && /*#__PURE__*/_jsx(UIRadioInput, {
            defaultChecked: hasInitialFilterSet,
            checked: selectedSendWithFiltersOption === SendWithFiltersOptions.PREVIOUS_SENT_FILTERS,
            onChange: () => {
              setSelectedSendWithFiltersOption(SendWithFiltersOptions.PREVIOUS_SENT_FILTERS);
              onApplyDashboardFilters(initialFilterSetId);
            },
            description: /*#__PURE__*/_jsx(UIHelpIcon, {
              title: /*#__PURE__*/_jsx(Small, {
                children: /*#__PURE__*/_jsx(ScrollContainer, {
                  maxHeight: "200px",
                  gradientColor: "text-core-default",
                  children: /*#__PURE__*/_jsx(DashboardFiltersGroupedByDataSourceList, {
                    filters: emailFilters
                  })
                })
              })
            }),
            descriptionLayout: "horizontal",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "email.dashboard.withPreviousSentFilters"
            })
          }), createEmailOnlyFilterSetRequestFailed && /*#__PURE__*/_jsx(UIAlert, {
            type: "danger",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "email.dashboard.currentAppliedFiltersFailed"
            })
          }), /*#__PURE__*/_jsx(UIRadioInput, {
            checked: selectedSendWithFiltersOption === SendWithFiltersOptions.CURRENT_APPLIED_FILTERS,
            disabled: !hasAppliedFilters || isUnsupportedDataExportType || createEmailOnlyFilterSetRequestFailed,
            onChange: () => {
              handleSendWithCurrentAppliedFilters();
            },
            description: /*#__PURE__*/_jsx(UIHelpIcon, {
              title: createEmailOnlyFilterSetRequestFailed ? /*#__PURE__*/_jsx(FormattedMessage, {
                message: "email.dashboard.currentAppliedFiltersFailed"
              }) : !isUnsupportedDataExportType ? /*#__PURE__*/_jsx(Small, {
                children: /*#__PURE__*/_jsx(AppliedDashboardFiltersList, {
                  dashboard: dashboard
                })
              }) : /*#__PURE__*/_jsx(FormattedMessage, {
                message: "email.dashboard.unsupportedDataTypeExportWarning"
              })
            }),
            descriptionLayout: "horizontal",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "email.dashboard.withImprovedFilters"
            })
          })]
        })
      })
    })
  });
};